/**
 * Helpers for merging server-returned session context into the copy we keep in
 * localStorage.
 *
 * The backend rebuilds session_context from its own ContextManager, which only
 * knows a fixed set of keys. Anything it doesn't know about (drafts, pending
 * resource ids, and any newer document type) is missing from its response, so
 * overwriting localStorage with that response silently drops active documents.
 * Merging keeps a local key whenever the server omitted it entirely, while
 * still honoring every value the server did send (including empty arrays,
 * which are real removals).
 */

export type SessionContext = Record<string, any>;

export const mergeServerSessionContext = (
  local: SessionContext | null | undefined,
  incoming: SessionContext | null | undefined
): SessionContext => {
  if (!incoming || typeof incoming !== 'object') return local || {};
  if (!local || typeof local !== 'object') return incoming;

  const merged: SessionContext = { ...incoming };

  Object.keys(local).forEach((key) => {
    if (!(key in incoming)) {
      merged[key] = local[key];
    }
  });

  return merged;
};

/**
 * Merge a server-returned session context into localStorage and notify the UI.
 * Returns the merged context that was stored.
 */
export const applyServerSessionContext = (incoming: SessionContext | null | undefined): SessionContext => {
  let local: SessionContext = {};
  try {
    const stored = localStorage.getItem('session_context');
    local = stored && stored !== 'undefined' ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Failed to parse stored session_context:', error);
  }

  const merged = mergeServerSessionContext(local, incoming);

  const droppedKeys = Object.keys(local).filter(
    (key) => incoming && typeof incoming === 'object' && !(key in incoming)
  );
  if (droppedKeys.length > 0) {
    console.warn('[sessionContext] Server response omitted keys, keeping local values:', droppedKeys);
  }

  localStorage.setItem('session_context', JSON.stringify(merged));
  window.dispatchEvent(new Event('storage-update'));

  return merged;
};

/** Counts of each active document type — used for save/load diagnostics. */
export const summarizeSessionContext = (context: SessionContext | null | undefined) => ({
  patents: context?.current_patents?.length || 0,
  publications: context?.current_publications?.length || 0,
  applications: context?.current_applications?.length || 0,
  foreign_patents: context?.current_foreign_patents?.length || 0,
  pros_docs: context?.pros_docs?.length || 0,
  uploads: context?.resource_names?.length || 0,
  drafts: context?.current_drafts?.length || 0,
});
